import React from 'react';
import { Select, Card, Group, Avatar, Text, Badge } from '@mantine/core';
import { User } from '../../types';

interface TeamRolePickerProps {
  label: string;
  description?: string;
  placeholder: string;
  users: User[];
  value: string;
  onChange: (userId: string) => void;
  badgeLabel: string;
  badgeColor?: string;
  required?: boolean;
  clearable?: boolean;
  error?: string;
  disabled?: boolean;
  mt?: string;
}

/**
 * Single reusable picker for a team role (lead / PM). Same control and logic for
 * both: a searchable Select keyed by user id, plus a preview card of the chosen
 * user with a role badge.
 */
export const TeamRolePicker: React.FC<TeamRolePickerProps> = ({
  label,
  description,
  placeholder,
  users,
  value,
  onChange,
  badgeLabel,
  badgeColor = 'teal',
  required = false,
  clearable = false,
  error,
  disabled = false,
  mt,
}) => {
  const selected = users.find((user) => user._id === value);

  return (
    <div>
      <Select
        label={label}
        description={description}
        placeholder={placeholder}
        data={users.map((user) => ({
          value: user._id,
          label: `${user.name} (${user.email})`,
        }))}
        value={value || null}
        onChange={(v) => onChange(v || '')}
        searchable
        clearable={clearable}
        required={required}
        error={error}
        disabled={disabled}
        mt={mt}
      />

      {selected && (
        <Card withBorder p="sm" mt="sm">
          <Group gap="sm">
            <Avatar src={selected.avatar} radius="xl" />
            <div style={{ flex: 1 }}>
              <Text size="sm" fw={500}>
                {selected.name}
              </Text>
              <Text size="xs" c="dimmed">
                {selected.position}
              </Text>
            </div>
            <Badge color={badgeColor} variant="light">
              {badgeLabel}
            </Badge>
          </Group>
        </Card>
      )}
    </div>
  );
};
